import { sanitizeValue } from '../../lib/sanitize';
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { BASE_URL_MAP } from '../../lib/environment';

type LaunchApplicationOption = {
  id: string;
  name: string;
};

export const launchMenuOptions: LaunchApplicationOption[] = [
  {
    id: 'page:https://appstore001.iptvtg.com/resCallerId/history.aspx?guid=$(dev)',
    name: 'Caller ID History',
  },
  {
    id: 'page:https://appstore001.iptvtg.com/resCallerId/settings.aspx?guid=$(dev)',
    name: 'Caller ID Settings',
  },
  {
    id: 'page:https://appstore001.iptvtg.com/resMessaging/inbox.aspx?guid=$(dev)',
    name: 'Inbox',
  },
  {
    id: 'action:menu',
    name: 'Menu',
  },
  {
    id: 'page:swimlanemenu?menuid=_settings',
    name: 'Settings',
  },
  {
    id: 'page:wifisettings',
    name: 'WiFi Settings',
  },
  {
    id: 'page:wifidiagnostics',
    name: 'WiFi Diagnostics',
  },
  {
    id: 'page:bootstrapdiag',
    name: 'Network Diagnostics',
  },
  {
    id: 'action:event#urn:microsoft:mediaroom:event:diagnostics:overlay',
    name: 'Stream Diagnostics',
  },
  {
    id: 'page:rfaddremote',
    name: 'Pair RF Remote',
  },
  {
    id: 'page:parentalcontrol_menu',
    name: 'Parental Controls',
  },
  {
    id: 'page:aspectratiosettings', //.xml",
    name: 'Aspect Ratio Settings',
  },
  // {
  //   id: 'page:yellow',
  //   name: 'Yellow',
  // },
  // {
  //   id: 'page:blue',
  //   name: 'Blue',
  // },
  // {
  //   id: 'page:red',
  //   name: 'Red',
  // },
  // {
  //   id: 'page:green',
  //   name: 'Green',
  // },
  /*{
    id: "action:recordedtv",
    name: "DVR",
  },
  {
    id: "action:favorites",
    name: "Favorites",
  },
  {
    id: "action:vod",
    name: "VOD",
  },
  {
    id: "page:gridguide",
    name: "Guide",
  },
  {
    id: "page:monkey",
    name: "Monkey",
  },*/
];

export const useDevice = (accountId: string | undefined, device: Device) => {
  const { loading, isSuccess, makeRequest, response, error, resetState } =
    useFetch();
  const operator = useOperator();

  const deviceType =
    operator.type === 'Residential'
      ? device.model.system === 'MR'
        ? 'MediaRoom Set-Top Box'
        : ''
      : (device.model.type as string);

  const CHANGE_CHANNEL_URL: Record<OperatorType, string> = {
    Residential: `${BASE_URL_MAP['Residential']}/${operator?.id}/Accounts/${accountId}/${device.mac}/ChangeChannel`,
    Hospitality: `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Accounts/${accountId}/MediaRoom/${device.id}/ChangeChannel`,
  };

  const changeChannel = (channel: string, useIcc: boolean) => {
    if (!accountId) return;
    if (!deviceType.toLowerCase().startsWith('mediaroom')) return;

    makeRequest(
      CHANGE_CHANNEL_URL[operator.type],
      'post',
      sanitizeValue({
        channel: channel,
        useICC: useIcc,
      })
    );
  };

  const REMOTE_DRA_URLS: Record<OperatorType, string> = {
    Residential: `${BASE_URL_MAP['Residential']}/${operator?.id}/Accounts/${accountId}/${device.mac}/RemoteDRA`,
    Hospitality: `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Accounts/${accountId}/MediaRoom/${device.id}/RemoteDRA`,
  };

  const resetMrPlayTvService = () => {
    if (operator.type === 'Residential') return;
    if (deviceType.toLowerCase() !== 'mediaroom play set-top box') return;

    makeRequest(MEDIAROOM_REBOOT_URL.Hospitality, 'post');
  };

  const clearCache = () => {
    if (operator.type === 'Residential') return;
    if (deviceType.toLowerCase() !== 'mediaroom play set-top box') return;

    makeRequest(REMOTE_DRA_URLS.Hospitality, 'post');
  };

  const remoteDRA = () => {
    if (!accountId) return;

    switch (deviceType.toLowerCase()) {
      case 'mediaroom set-top box':
        makeRequest(REMOTE_DRA_URLS[operator.type], 'post');
        break;
      case 'mediaroom play set-top box':
        makeRequest(
          `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Amino/${device.id}/FactoryReset`,
          'post'
        );
        break;
      default:
        break;
    }
  };

  const launchSettingsApplication = (url: string) => {
    if (!accountId) return;

    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator?.id}/Accounts/${accountId}/${device.mac}/LaunchSettingsApplication`,
      'post',
      {
        Url: url,
      }
    );
  };

  const resetApps = () => {
    if (!accountId) return;
    if (deviceType.toLowerCase() !== 'mediaroom play set-top box') return;

    makeRequest(
      `${BASE_URL_MAP[operator.type]}/${operator?.id}/Amino/${
        device.id
      }/AppReset`,
      'post'
    );
  };

  const MEDIAROOM_REBOOT_URL: Record<OperatorType, string> = {
    Residential: `${BASE_URL_MAP['Residential']}/${operator?.id}/Accounts/${accountId}/${device.mac}/Reboot`,
    Hospitality: `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Accounts/${accountId}/MediaRoom/${device.id}/Reboot`,
  };

  const reboot = () => {
    if (!accountId) return;

    switch (deviceType.toLowerCase()) {
      case 'access point':
        makeRequest(
          `${BASE_URL_MAP[operator.type]}/${operator?.id}/AccessPoints/Reboot/${
            device.id
          }`,
          'put'
        );
        break;
      case 'mediaroom play set-top box':
        makeRequest(
          `${BASE_URL_MAP[operator.type]}/${operator?.id}/Amino/${
            device.id
          }/Reboot`,
          'post'
        );
        break;
      case 'mediaroom set-top box':
        makeRequest(MEDIAROOM_REBOOT_URL[operator.type], 'post');
        break;
      case 'cable modem':
        makeRequest(
          `${BASE_URL_MAP['Hospitality']}/${operator?.id}/SNMP/${device.id}/Reboot`,
          'post'
        );
        break;
      default:
        break;
    }
  };

  const toggleCasting = (enabled: boolean) => {
    if (!accountId) return;

    makeRequest(
      `${BASE_URL_MAP['Hospitality']}/${operator?.id}/Amino/${device.id}/Casting`,
      'PUT',
      sanitizeValue({ Enable: enabled })
    );
  };

  return {
    error,
    isSuccess,
    changeChannel,
    clearCache,
    launchSettingsApplication,
    loading,
    reboot,
    remoteDRA,
    resetApps,
    resetTvService: resetMrPlayTvService,
    response,
    toggleCasting,
    resetState,
  };
};
